// Reusable user menu component with dropdown
// CREATED: Extracted from Layout.tsx and DesktopSidebar.tsx for consistency
// UPDATED: Enhanced menu positioning and animations
// UPDATED: Integrated role-based theme system for consistent styling
// UPDATED: Fixed border color to use neutral gray (border-gray-100) for visual consistency with sidebar borders
// Features: Avatar with initials, role badge, dropdown menu with password change and logout
// Menu opens to the right with enhanced UX:
// - Increased vertical height (py-4 padding)
// - Shifted left for better positioning (-ml-6)
// - Elegant slide, fade and scale animation on appear
// UPDATED: Removed collapsed state - menu always shows in expanded state

import { useState } from 'react';
import { ChevronsUpDown, LogOut, KeyRound } from 'lucide-react';
import { getRoleTheme, type UserRole } from '../../lib/roleThemes';
import RoleBadge from '../ui/RoleBadge';

interface UserMenuProps {
  userName: string;
  userInitials: string;
  userRole: UserRole;
  onPasswordChange: () => void;
  onLogout: () => void;
}

export function UserMenu({
  userName,
  userInitials,
  userRole,
  onPasswordChange,
  onLogout
}: UserMenuProps) {
  const [showMenu, setShowMenu] = useState(false);
  const theme = getRoleTheme(userRole);

  return (
    <div className="p-2 border-t border-gray-100 relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-all duration-200"
      >
        <div
          className={`w-10 h-10 text-sm rounded-full bg-gradient-to-br ${theme.avatarGradient} flex items-center justify-center text-white font-bold flex-shrink-0`}
        >
          {userInitials}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-medium truncate">{userName}</p>
          <div className="mt-1">
            <RoleBadge role={userRole} size="sm" />
          </div>
        </div>
        <ChevronsUpDown size={20} className="flex-shrink-0 text-gray-500" />
      </button>

      {/* User Menu Dropdown */}
      {showMenu && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[58]"
            onClick={() => setShowMenu(false)}
          />

          {/* Menu - Opens to the right with elegant animation */}
          <div className="absolute bottom-0 left-full -ml-6 w-[260px] bg-white border border-gray-200 rounded-lg shadow-xl z-[60] animate-menu-appear">
            <button
              onClick={() => {
                setShowMenu(false);
                onPasswordChange();
              }}
              className="w-full flex items-center gap-3 px-5 py-4 text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-t-lg whitespace-nowrap"
            >
              <KeyRound size={20} className="text-gray-500 flex-shrink-0" />
              <span>Changer le mot de passe</span>
            </button>
            <button
              onClick={() => {
                setShowMenu(false);
                onLogout();
              }}
              className="w-full flex items-center gap-3 px-5 py-4 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100 rounded-b-lg whitespace-nowrap"
            >
              <LogOut size={20} className="flex-shrink-0" />
              <span>Déconnexion</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
